import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Copy,
  Plus,
  Trash2,
  X,
} from '../bravosIcons';
import { useAuth } from '../../../contexts/AuthContext';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { PAGE_CHROME_LOADER_BRAND_GREEN, TRAINER_PAGE_BG } from '../../../constants/pageChrome';
import { usePageStatusBar } from '../../../hooks/usePageStatusBar';
import { WorkoutService } from '../../../lib/workoutService';
import { displayNameFromProfile } from '../../../pages/account/accountShared';
import { toLocalDateKey } from '../../../utils/dateKey';
import { TrainerAppLayout } from '../shell/TrainerAppLayout';
import { createTrainerDrillDownSectionSelect } from '../shell/trainerSectionNav';
import { useAssignMembersCache } from '../dashboard/hooks/useAssignMembersCache';
import { AssignWorkoutPlanModal } from '../dashboard/overlays/AssignWorkoutPlanModal';
import { TrainerCalAdd } from '../calendar/TrainerCalAdd';
import { TrainerCalRest } from '../calendar/TrainerCalRest';
import { formatSessionCardMetaCompact } from '../members/memberDetail/calendarInteractions';
import { TrainerActionButton } from '../components/TrainerActionButton';
import { TrainerModeToggle } from '../components/TrainerModeToggle';
import { TrainerConfirmDialog } from './overlays/TrainerConfirmDialog';
import { TrainerWorkoutLibraryDrawer } from '../overlays/TrainerWorkoutLibraryDrawer';
import { PlanTrimDaysDialog } from './overlays/PlanTrimDaysDialog';
import { PlanBuilderToast } from './overlays/PlanBuilderToast';
import { PlanClipBar, PlanEditBar, PlanSelectionBar } from './overlays/PlanBuilderActionBars';
import { ExerciseEditSavedToast } from './overlays/ExerciseEditSavedToast';
import { IconCopy, IconTrash } from './TrainerWorkoutDetailEditIcons';
import {
  useTrainerWorkoutPlanBuilderState,
  type PlanBuilderMode,
} from './hooks/useTrainerWorkoutPlanBuilderState';
import { dayHasWorkouts } from './utils/planBuilderDays';
import './TrainerWorkoutDetailPage.css';
import './TrainerWorkoutPlanBuilder.css';
import '../dashboard/overlays/trainer-dashboard-overlays.css';

/** Align with Member Detail calendar visible-card cap. */
const MAX_VISIBLE_CARDS = 1;

export const WorkoutPlanDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useAuth();

  const isNewPlan = id === 'new' || location.pathname === '/trainer-dashboard/workout-plan/new';
  const initialMode: PlanBuilderMode =
    !isNewPlan && searchParams.get('mode') === 'edit' ? 'edit' : isNewPlan ? 'create' : 'view';

  const setModeInUrl = useCallback(
    (mode: PlanBuilderMode) => {
      if (isNewPlan) return;
      const next = new URLSearchParams(searchParams);
      if (mode === 'edit') next.set('mode', 'edit');
      else next.delete('mode');
      setSearchParams(next, { replace: true });
    },
    [isNewPlan, searchParams, setSearchParams],
  );

  const builder = useTrainerWorkoutPlanBuilderState({
    isNewPlan,
    planId: isNewPlan ? undefined : id,
    trainerId: profile?.id,
    initialMode,
    navigate,
    locationState: location.state,
    onModeChange: setModeInUrl,
  });

  const assignMembersCache = useAssignMembersCache(profile?.id);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAssignMemberId, setSelectedAssignMemberId] = useState('');
  const [assignmentStartDate, setAssignmentStartDate] = useState<string>(() =>
    toLocalDateKey(new Date()),
  );
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [assignSuccessToast, setAssignSuccessToast] = useState<string | null>(null);

  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [discardViewConfirm, setDiscardViewConfirm] = useState(false);
  const [planDeleteConfirm, setPlanDeleteConfirm] = useState(false);
  const [duplicateInFlight, setDuplicateInFlight] = useState(false);
  const [duplicateToastOpen, setDuplicateToastOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null);
  const [visibleCardLimit, setVisibleCardLimit] = useState(MAX_VISIBLE_CARDS);
  const [daysInput, setDaysInput] = useState('7');
  const [daysInputFocused, setDaysInputFocused] = useState(false);
  const daygridsRef = useRef<HTMLDivElement | null>(null);
  const planNameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!daysInputFocused) setDaysInput(String(builder.totalDays));
  }, [builder.totalDays, daysInputFocused]);

  useEffect(() => {
    if (!builder.nameError) return;
    const el = planNameInputRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      const main = el.closest('.twd-main') as HTMLElement | null;
      if (main) {
        const mainRect = main.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const delta =
          elRect.top - mainRect.top - main.clientHeight / 2 + elRect.height / 2;
        main.scrollBy({ top: delta, behavior: 'smooth' });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      el.focus({ preventScroll: true });
    });
  }, [builder.nameError, builder.nameErrorSeq]);

  const commitDaysInput = useCallback(() => {
    const n = parseInt(daysInput.replace(/\D/g, ''), 10);
    if (Number.isNaN(n)) {
      setDaysInput(String(builder.totalDays));
      return;
    }
    builder.requestTotalDaysChange(n);
  }, [daysInput, builder.totalDays, builder.requestTotalDaysChange]);

  usePageStatusBar({
    background: builder.loading ? PAGE_CHROME_LOADER_BRAND_GREEN : TRAINER_PAGE_BG,
  });

  useEffect(() => {
    if (!builder.isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [builder.isDirty]);

  useEffect(() => {
    setVisibleCardLimit(MAX_VISIBLE_CARDS);
  }, [id, builder.mode, builder.days.length]);

  // Equal-height day cells within each week grid (Member calendar pattern)
  useLayoutEffect(() => {
    const root = daygridsRef.current;
    if (!root) return;
    root.querySelectorAll<HTMLElement>('.twp-daygrid').forEach((grid) => {
      const dayEls = grid.querySelectorAll<HTMLElement>('.twp-day');
      dayEls.forEach((el) => {
        el.style.height = 'auto';
      });
      let maxH = 100;
      dayEls.forEach((el) => {
        maxH = Math.max(maxH, el.scrollHeight);
      });
      dayEls.forEach((el) => {
        el.style.height = `${maxH}px`;
      });
    });
  }, [
    builder.weeks,
    builder.days,
    builder.pasteMode,
    builder.selectedDayIndices,
    visibleCardLimit,
    builder.isEditable,
  ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (builder.pendingTrim) {
        builder.cancelTrim();
        return;
      }
      if (builder.pasteMode) {
        builder.endPaste(false);
        return;
      }
      if (builder.drawerOpen) {
        builder.closeDrawer();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [builder]);

  const backToLibrary = useCallback(() => {
    try {
      localStorage.setItem('trainer_dashboard_active_section', 'library');
      localStorage.setItem('trainer_dashboard_active_tab', 'workoutPlans');
    } catch {
      // ignore
    }
    navigate('/trainer-dashboard');
  }, [navigate]);

  const handleConfirmDeletePlan = async () => {
    if (!id || isNewPlan) return;
    try {
      await WorkoutService.deleteWorkoutPlan(id);
      setPlanDeleteConfirm(false);
      backToLibrary();
    } catch (err) {
      console.error('Delete workout plan:', err);
      alert('Failed to delete workout plan. Please try again.');
    }
  };

  const handleDuplicatePlan = async () => {
    if (!id || isNewPlan || duplicateInFlight) return;
    setDuplicateInFlight(true);
    setDuplicateToastOpen(true);
    try {
      const dup = await WorkoutService.duplicateTrainerWorkoutPlan(id);
      navigate(`/trainer-dashboard/workout-plan/${dup.newPlanId}?mode=edit`);
    } catch (err) {
      console.error('Duplicate workout plan:', err);
      setDuplicateToastOpen(false);
      alert(err instanceof Error ? err.message : 'Failed to duplicate workout plan. Please try again.');
    } finally {
      setDuplicateInFlight(false);
    }
  };

  const requestLeave = useCallback(
    (go: () => void) => {
      if (builder.isDirty && builder.isEditable) {
        setPendingNav(() => go);
        setLeaveConfirmOpen(true);
        return;
      }
      go();
    },
    [builder.isDirty, builder.isEditable],
  );

  const handleRailSelect = createTrainerDrillDownSectionSelect(navigate);
  const wrappedRailSelect = (section: 'members' | 'library' | 'chat' | 'control') => {
    if (section === 'chat') {
      handleRailSelect(section);
      return;
    }
    requestLeave(() => handleRailSelect(section));
  };

  const enterEditMode = () => {
    builder.setMode('edit');
    setModeInUrl('edit');
  };

  const requestViewMode = () => {
    const result = builder.requestModeChange('view');
    if (result === 'confirm-discard') {
      setDiscardViewConfirm(true);
      return;
    }
    setModeInUrl('view');
  };

  const handleDayClick = (dayIndex: number) => {
    if (builder.pasteMode) {
      builder.pasteToDay(dayIndex);
      return;
    }

    if (builder.isEditable) {
      builder.toggleDaySelection(dayIndex);
      return;
    }
  };

  const handleWorkoutSlotClick = (workoutId: string) => {
    if (builder.pasteMode || builder.isEditable) return;
    requestLeave(() => {
      if (!isNewPlan && id) {
        navigate(`/trainer-dashboard/workout-plan/${id}/workout/${workoutId}`);
      } else {
        navigate(`/trainer-dashboard/workout/${workoutId}`);
      }
    });
  };

  const handleAssignPlanOpen = async () => {
    if (!id || isNewPlan) return;
    setShowAssignModal(true);
    setSelectedAssignMemberId('');
    setAssignmentStartDate(toLocalDateKey(new Date()));
    setAssignmentNotes('');
    setAssignmentError(null);
    const loadError = await assignMembersCache.loadAssignMembersIfNeeded();
    if (loadError) setAssignmentError(loadError);
  };

  const handleAssignPlanSubmit = async () => {
    if (!id || isNewPlan) return;
    if (!selectedAssignMemberId) {
      setAssignmentError('Please select a member');
      return;
    }
    if (!assignmentStartDate) {
      setAssignmentError('Please select a start date');
      return;
    }
    try {
      setIsAssigning(true);
      setAssignmentError(null);
      await WorkoutService.assignPlanToMember({
        userId: selectedAssignMemberId,
        workoutPlanId: id,
        startDate: assignmentStartDate,
        notes: assignmentNotes || undefined,
      });
      const member = assignMembersCache.assignMembers.find(
        (m) => m.memberId === selectedAssignMemberId,
      );
      const memberName =
        (member && (displayNameFromProfile(member.member) || member.member?.email)) || 'member';
      setShowAssignModal(false);
      setAssignSuccessToast(`Assigned to ${memberName}`);
    } catch (err: unknown) {
      setAssignmentError(
        err instanceof Error ? err.message : 'Failed to assign workout plan. Please try again.',
      );
    } finally {
      setIsAssigning(false);
    }
  };

  const breadcrumbFallback = isNewPlan
    ? 'New plan'
    : builder.planName.trim() || 'Workout plan';

  if (builder.loading) {
    return (
      <TrainerAppLayout
        activeSection="library"
        onSectionSelect={wrappedRailSelect}
        shellClassName="twd-page twd-page-with-rail twp-page"
        mainClassName="twd-main flex-1 min-w-0"
      >
        <div className="twp-loading">
          <LoadingSpinner />
        </div>
      </TrainerAppLayout>
    );
  }

  if (builder.error && !isNewPlan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-page)]">
        <div className="text-center px-4">
          <h1 className="text-xl font-bold mb-2">Error</h1>
          <p className="text-gray-600 mb-4">{builder.error}</p>
          <button type="button" className="twp-btn-primary" onClick={backToLibrary}>
            Back to Workout Plans
          </button>
        </div>
      </div>
    );
  }

  const nameValid = builder.planName.trim().length > 0;
  const createReady = nameValid;
  const createBarBadge = createReady
    ? { className: 'create-bar-badge create-badge-ready', text: 'Ready to create' }
    : { className: 'create-bar-badge create-badge-warn', text: 'Name required' };
  const createBarHelper = createReady
    ? builder.totalWorkoutSlots === 0
      ? 'You can add workouts after creating'
      : `${builder.totalWorkoutSlots} workout${builder.totalWorkoutSlots === 1 ? '' : 's'} scheduled`
    : 'Required: Plan name';

  const showCreateBar = isNewPlan && !builder.pasteMode && !builder.drawerOpen;
  const showSaveBar =
    !isNewPlan &&
    builder.isDirty &&
    builder.isEditable &&
    !builder.pasteMode &&
    !builder.drawerOpen;

  const shellClass = `twd-page twd-page-with-rail twp-page${builder.pasteMode ? ' twp-paste-mode' : ''}${builder.isEditable ? ' twp-page--editing' : ''}${builder.mode === 'edit' ? ' twp-page--edit' : ''}${isNewPlan ? ' twp-page--create' : ''}`;

  const libraryAssignTargets = [...builder.assignTargets].sort((a, b) => a - b);
  const libraryIsReplace = builder.assignMode === 'replace';
  const libraryContextLine = libraryIsReplace ? (
    <>
      Swapping workout in Day {libraryAssignTargets[0] + 1}
      {builder.assignReplaceIndex != null ? <> · slot {builder.assignReplaceIndex + 1}</> : null}
    </>
  ) : libraryAssignTargets.length === 1 ? (
    <>Adding to Day {libraryAssignTargets[0] + 1}</>
  ) : libraryAssignTargets.length > 1 ? (
    <>
      Adding to {libraryAssignTargets.length} days
      {': '}
      {libraryAssignTargets.map((i) => `Day ${i + 1}`).join(', ')}
    </>
  ) : (
    'Adding workout'
  );

  const daysStepper = (
    <span className="twp-daysfield twp-daysfield--inline">
      <div className="twp-stepper">
        <button
          type="button"
          aria-label="Fewer days"
          disabled={builder.totalDays <= 1}
          onClick={() => builder.requestTotalDaysChange(builder.totalDays - 1)}
        >
          &minus;
        </button>
        <input
          type="text"
          inputMode="numeric"
          className="tnum"
          aria-label="Number of days"
          value={daysInput}
          onFocus={(e) => {
            setDaysInputFocused(true);
            e.currentTarget.select();
          }}
          onChange={(e) => {
            setDaysInput(e.target.value.replace(/\D/g, '').slice(0, 2));
          }}
          onBlur={() => {
            setDaysInputFocused(false);
            commitDaysInput();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
        <button
          type="button"
          aria-label="More days"
          disabled={builder.totalDays >= 84}
          onClick={() => builder.requestTotalDaysChange(builder.totalDays + 1)}
        >
          +
        </button>
      </div>
      <span>days</span>
    </span>
  );

  const readOnlyStats = (
    <>
      <div className="twp-stat tnum">
        <span>
          <strong>{builder.totalWorkoutSlots}</strong> workouts
        </span>
      </div>
      <span className="twp-meta-divider" aria-hidden="true" />
      <div className="twp-stat tnum">
        <span>
          <strong>{builder.restDays}</strong> rest
        </span>
      </div>
      <span className="twp-meta-divider" aria-hidden="true" />
      <div className="twp-stat tnum">
        <span>
          ~<strong>{builder.totalMins}</strong> min total
        </span>
      </div>
    </>
  );

  const breadcrumb = (
    <nav className="twd-breadcrumb" aria-label="Breadcrumb">
      <Link
        to="/trainer-dashboard"
        onClick={(e) => {
          e.preventDefault();
          requestLeave(backToLibrary);
        }}
      >
        Library
      </Link>
      <span className="twd-bc-sep" aria-hidden="true">
        /
      </span>
      <Link
        to="/trainer-dashboard"
        onClick={(e) => {
          e.preventDefault();
          requestLeave(backToLibrary);
        }}
      >
        Workout Plans
      </Link>
      <span className="twd-bc-sep" aria-hidden="true">
        /
      </span>
      <span className="twd-bc-current">{isNewPlan ? 'New plan' : breadcrumbFallback}</span>
    </nav>
  );

  return (
    <TrainerAppLayout
      activeSection="library"
      onSectionSelect={wrappedRailSelect}
      shellClassName={shellClass}
      mainClassName="twd-main flex-1 min-w-0"
    >
      {isNewPlan ? (
        <div className="twd-topbar">{breadcrumb}</div>
      ) : (
        <div className="twd-topbar">
          {breadcrumb}
          <div className="twd-topbar-actions">
            <TrainerActionButton
              variant="primary"
              icon={<Plus width={14} height={14} aria-hidden />}
              onClick={handleAssignPlanOpen}
            >
              Assign plan
            </TrainerActionButton>

            <TrainerModeToggle
              mode={builder.mode === 'edit' ? 'edit' : 'view'}
              onView={requestViewMode}
              onEdit={enterEditMode}
              className="twp-mode-toggle"
            />
          </div>
        </div>
      )}

      {isNewPlan ? (
        <>
          <div className="create-banner">
            <div className="create-banner-left">
              <span className="edit-dot" aria-hidden="true" />
              <span className="create-banner-text">
                <strong>New plan.</strong> Give it a name and length, then program each day.
              </span>
            </div>
          </div>

          <section className="title-card" aria-label="Plan details">
            <label className="title-card-label title-card-label--name" htmlFor="twp-create-name">
              Plan name <span className="title-card-required">*</span>
            </label>
            <div
              className={`title-card-name-wrap${builder.nameError ? ' title-card-name-wrap--error' : ''}`}
            >
              <input
                id="twp-create-name"
                ref={planNameInputRef}
                className="title-card-name"
                value={builder.planName}
                placeholder="Name this plan…"
                spellCheck={false}
                aria-invalid={Boolean(builder.nameError)}
                aria-describedby={builder.nameError ? 'twp-plan-name-error' : undefined}
                onChange={(e) => builder.setPlanName(e.target.value)}
                onBlur={() => builder.setPlanName(builder.planName.trim())}
              />
            </div>
            {builder.nameError ? (
              <p id="twp-plan-name-error" className="title-card-name-error" role="alert">
                {builder.nameError}
              </p>
            ) : null}
            <div className="title-card-meta twp-plan-meta">
              <div className="twp-stat">{daysStepper}</div>
              <span className="twp-meta-divider" aria-hidden="true" />
              {readOnlyStats}
            </div>
          </section>
        </>
      ) : builder.mode === 'edit' ? (
        <>
          <div className="edit-banner">
            <div className="edit-banner-left">
              <span className="edit-dot" aria-hidden="true" />
              <span className="edit-banner-text">
                <strong>Editing plan.</strong>
              </span>
            </div>
            <div className="edit-banner-actions">
              <button
                type="button"
                className="twp-edit-banner-btn twp-edit-banner-btn--danger"
                onClick={() => setPlanDeleteConfirm(true)}
              >
                <IconTrash />
                Delete plan
              </button>
              <button
                type="button"
                className="twp-edit-banner-btn twp-edit-banner-btn--ghost"
                disabled={duplicateInFlight}
                onClick={() => void handleDuplicatePlan()}
              >
                <IconCopy />
                Duplicate
              </button>
            </div>
          </div>

          <section className="twp-plan-details" aria-label="Plan details">
            <label className="title-card-label title-card-label--name" htmlFor="twp-edit-name">
              Plan name <span className="title-card-required">*</span>
            </label>
            <div
              className={`title-card-name-wrap${builder.nameError ? ' title-card-name-wrap--error' : ''}`}
            >
              <input
                id="twp-edit-name"
                ref={planNameInputRef}
                className="title-card-name"
                value={builder.planName}
                placeholder="Plan name"
                spellCheck={false}
                aria-invalid={Boolean(builder.nameError)}
                aria-describedby={builder.nameError ? 'twp-plan-name-error' : undefined}
                onChange={(e) => builder.setPlanName(e.target.value)}
                onBlur={() => builder.setPlanName(builder.planName.trim())}
              />
            </div>
            {builder.nameError ? (
              <p id="twp-plan-name-error" className="title-card-name-error" role="alert">
                {builder.nameError}
              </p>
            ) : null}
            <div className="twp-plan-meta">
              <div className="twp-stat">{daysStepper}</div>
              <span className="twp-meta-divider" aria-hidden="true" />
              {readOnlyStats}
            </div>
          </section>
        </>
      ) : (
        <header className="twp-plan-header">
          <div className="twp-plan-meta">
            <div className="twp-stat">
              <span>
                <strong className="tnum">{builder.totalDays}</strong> days
              </span>
            </div>
            <span className="twp-meta-divider" aria-hidden="true" />
            {readOnlyStats}
          </div>
        </header>
      )}


      <div ref={daygridsRef}>
      {builder.weeks.map((week) => {
        const programmedInWeek = week.indices.filter((i) => dayHasWorkouts(builder.days[i])).length;
        const isExpandedGrid = visibleCardLimit > MAX_VISIBLE_CARDS;
        return (
          <section key={week.weekNum} className="twp-week">
            <div className="twp-week-head">
              <div className="twp-week-title">
                Week {week.weekNum}{' '}
                <span>
                  (Days {week.startDay} – {week.endDay})
                </span>
              </div>
              <div className="twp-week-mini tnum">
                {programmedInWeek} / {week.indices.length} programmed
              </div>
            </div>
            <div className="twp-daygrid">
              {week.indices.map((dayIndex) => {
                const day = builder.days[dayIndex];
                const slots = day?.workouts ?? [];
                const filled = slots.length > 0;
                const isSource = builder.copySourceDayIndex === dayIndex;
                const editable = builder.isEditable;
                const isSelected = builder.selectedDayIndices.has(dayIndex);
                const visibleSlots = slots.slice(0, visibleCardLimit);
                const overflow = Math.max(0, slots.length - visibleCardLimit);

                return (
                  <div
                    key={dayIndex}
                    className={[
                      'twp-day',
                      editable ? 'twp-day--editable' : '',
                      filled ? 'twp-day--filled' : 'twp-day--empty',
                      isSource ? 'twp-day--source' : '',
                      isSelected ? 'twp-day--selected' : '',
                      isExpandedGrid ? 'is-expanded' : '',
                      builder.pasteMode && builder.pasteHintOffDays.has(dayIndex)
                        ? 'twp-day--paste-hint-off'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => handleDayClick(dayIndex)}
                    onMouseLeave={() => {
                      if (builder.pasteMode) builder.clearPasteHintOff(dayIndex);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDayClick(dayIndex);
                      }
                    }}
                    role={editable ? 'button' : undefined}
                    tabIndex={editable ? 0 : undefined}
                  >
                    <div className="twp-day-num">
                      <span className="twp-day-idx tnum">{dayIndex + 1}</span>
                      {editable ? (
                        <span className="twp-checkdot" aria-hidden="true">
                          {isSelected ? '✓' : ''}
                        </span>
                      ) : null}
                    </div>

                    {filled ? (
                      <>
                        <div className="twp-wk-list">
                          {visibleSlots.map((slot, slotIndex) =>
                            slot.workout ? (
                              <div
                                key={`${dayIndex}-${slotIndex}-${slot.workoutId}`}
                                className="twp-wk-item"
                                onClick={(e) => {
                                  if (builder.pasteMode) {
                                    e.stopPropagation();
                                    builder.pasteToDay(dayIndex);
                                    return;
                                  }
                                  e.stopPropagation();
                                  handleWorkoutSlotClick(slot.workoutId);
                                }}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (editable) return;
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleWorkoutSlotClick(slot.workoutId);
                                  }
                                }}
                                role={!editable ? 'button' : undefined}
                                tabIndex={!editable ? 0 : undefined}
                              >
                                {editable && !builder.pasteMode ? (
                                  <div
                                    className="twp-wk-item-actions"
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      className="twp-icon-btn twp-icon-btn--compact"
                                      aria-label="Copy workout"
                                      data-hint="Copy"
                                      onClick={() => builder.enterPasteMode(dayIndex, slotIndex)}
                                    >
                                      <Copy size={13} strokeWidth={2} />
                                    </button>
                                    <button
                                      type="button"
                                      className="twp-icon-btn twp-icon-btn--compact twp-icon-btn--danger"
                                      aria-label="Delete workout"
                                      data-hint="Delete"
                                      onClick={() => builder.removeWorkoutSlot(dayIndex, slotIndex)}
                                    >
                                      <Trash2 size={13} strokeWidth={2} />
                                    </button>
                                  </div>
                                ) : null}
                                <div className="twp-wk-item-name">{slot.workout.name}</div>
                                <div
                                  className="twp-wk-item-meta tnum"
                                  title={formatSessionCardMetaCompact(
                                    slot.workout.exerciseCount,
                                    slot.workout.estimatedDurationMinutes ?? 0,
                                  )}
                                >
                                  {formatSessionCardMetaCompact(
                                    slot.workout.exerciseCount,
                                    slot.workout.estimatedDurationMinutes ?? 0,
                                  )}
                                </div>
                              </div>
                            ) : null,
                          )}
                        </div>
                        {overflow > 0 ? (
                          <button
                            type="button"
                            className="twp-cal-more"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (builder.pasteMode) return;
                              setVisibleCardLimit((prev) => Math.max(prev, slots.length));
                            }}
                          >
                            +{overflow} more
                          </button>
                        ) : null}
                        {isExpandedGrid &&
                        overflow === 0 &&
                        slots.length > MAX_VISIBLE_CARDS ? (
                          <button
                            type="button"
                            className="twp-cal-more"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setVisibleCardLimit(MAX_VISIBLE_CARDS);
                            }}
                          >
                            Show less
                          </button>
                        ) : null}
                        {editable && !builder.pasteMode ? (
                          <TrainerCalAdd
                            onClick={(e) => {
                              e.stopPropagation();
                              builder.openDrawerForDays([dayIndex], 'add');
                            }}
                          />
                        ) : null}
                      </>
                    ) : (
                      <>
                        <TrainerCalRest />
                        {editable && !builder.pasteMode ? (
                          <TrainerCalAdd
                            onClick={(e) => {
                              e.stopPropagation();
                              builder.openDrawerForDays([dayIndex], 'add');
                            }}
                          />
                        ) : null}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
      </div>

      {showCreateBar ? (
        <div className="create-bar-dock">
          <div className="create-bar" role="region" aria-label="Create plan">
            <div className="create-bar-left">
              <span className={createBarBadge.className}>{createBarBadge.text}</span>
              <span className="create-bar-helper">{createBarHelper}</span>
            </div>
            <div className="create-bar-actions">
              <button
                type="button"
                className="twp-save-bar-btn twp-save-bar-btn--ghost"
                onClick={() => requestLeave(backToLibrary)}
              >
                Discard
              </button>
              <button
                type="button"
                className="twp-save-bar-btn twp-save-bar-btn--save"
                disabled={!createReady || builder.isSaving}
                onClick={() => void builder.handleCreate()}
              >
                {builder.isSaving ? 'Creating…' : 'Create plan'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showSaveBar ? (
        <PlanEditBar
          visible
          isSaving={builder.isSaving}
          onDiscard={() => {
            requestLeave(() => {
              builder.resetToOriginal();
              builder.setMode('view');
              setModeInUrl('view');
            });
          }}
          onSave={() => {
            void builder.handleSave();
          }}
        />
      ) : null}

      {builder.selectedDayIndices.size > 0 && builder.isEditable && !builder.pasteMode ? (
        <PlanSelectionBar
          visible
          selectedCount={builder.selectedDayIndices.size}
          onAddWorkout={() =>
            builder.openDrawerForDays(Array.from(builder.selectedDayIndices), 'add')
          }
          onMarkRest={builder.bulkMarkRest}
          onClear={builder.clearSelection}
        />
      ) : null}

      {builder.pasteMode && builder.clipboard ? (
        <PlanClipBar
          workout={builder.clipboard}
          pasteCount={builder.pasteCount}
          onDone={() => builder.endPaste(true)}
        />
      ) : null}

      <TrainerWorkoutLibraryDrawer
        open={builder.drawerOpen}
        title={libraryIsReplace ? 'Swap workout' : 'Add a workout'}
        contextLine={libraryContextLine}
        workouts={builder.trainerWorkouts}
        loading={builder.loadingWorkouts}
        onClose={builder.closeDrawer}
        onCreateWorkout={builder.navigateCreateWorkout}
        onAssign={(workout) => {
          builder.assignWorkoutToTargets(workout);
        }}
      />

      {builder.pendingTrim ? (
        <PlanTrimDaysDialog
          open
          currentDays={builder.totalDays}
          targetDays={builder.pendingTrim.targetDays}
          lostIndices={builder.pendingTrim.lostIndices}
          onConfirm={builder.confirmTrim}
          onCancel={builder.cancelTrim}
        />
      ) : null}

      {builder.toast ? (
        <PlanBuilderToast
          toast={builder.toast}
          onDismiss={builder.dismissToast}
          onUndo={builder.toast.onUndo}
          aboveSaveBar={showCreateBar || showSaveBar}
        />
      ) : null}

      {builder.saveSuccessToast ? (
        <ExerciseEditSavedToast
          variant="plan-saved"
          exerciseName=""
          planName={builder.planName}
          onDismiss={() => builder.setSaveSuccessToast(null)}
        />
      ) : null}

      {assignSuccessToast ? (
        <ExerciseEditSavedToast
          variant="assign-plan"
          exerciseName=""
          planName={builder.planName}
          memberDisplayName={assignSuccessToast.replace('Assigned to ', '')}
          onDismiss={() => setAssignSuccessToast(null)}
        />
      ) : null}

      {!isNewPlan && id ? (
        <AssignWorkoutPlanModal
          open={showAssignModal}
          assigningPlanId={id}
          planName={builder.planName}
          assignMembers={assignMembersCache.assignMembers}
          loadingAssignMembers={assignMembersCache.loadingAssignMembers}
          selectedAssignMemberId={selectedAssignMemberId}
          onSelectedAssignMemberIdChange={setSelectedAssignMemberId}
          assignmentStartDate={assignmentStartDate}
          onAssignmentStartDateChange={setAssignmentStartDate}
          assignmentNotes={assignmentNotes}
          onAssignmentNotesChange={setAssignmentNotes}
          assignmentError={assignmentError}
          isAssigning={isAssigning}
          onClose={() => setShowAssignModal(false)}
          onSubmit={handleAssignPlanSubmit}
          onAddMember={() => {
            setShowAssignModal(false);
            navigate('/trainer-dashboard', { state: { openAddMember: true } });
          }}
        />
      ) : null}

      {builder.showTemplateUpdateDialog && builder.pendingSave ? (
        <div className="lib-confirm-scrim" role="presentation">
          <div className="lib-confirm-dialog" role="alertdialog" aria-modal="true">
            <button
              type="button"
              className="tr-dialog__close"
              aria-label="Close"
              disabled={builder.isSaving}
              onClick={() => builder.setShowTemplateUpdateDialog(false)}
            >
              <X width={16} height={16} aria-hidden />
            </button>
            <h3>Update Plan</h3>
            <p>
              There {builder.activeInstances.length === 1 ? 'is' : 'are'}{' '}
              {builder.activeInstances.length}{' '}
              {builder.activeInstances.length === 1 ? 'person' : 'people'} currently assigned to
              this plan. Apply changes to their workout plans?
            </p>
            <div className="mb-3 max-h-40 overflow-y-auto border border-gray-200 rounded-md">
              {builder.activeInstances.map((instance) => (
                <label
                  key={instance.instanceId}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={builder.selectedInstanceIds.has(instance.instanceId)}
                    onChange={(e) => {
                      const next = new Set(builder.selectedInstanceIds);
                      if (e.target.checked) next.add(instance.instanceId);
                      else next.delete(instance.instanceId);
                      builder.setSelectedInstanceIds(next);
                    }}
                  />
                  <span className="text-sm font-medium">{instance.memberName}</span>
                </label>
              ))}
            </div>
            <div className="lib-confirm-actions">
              <button
                type="button"
                className="lib-confirm-btn"
                disabled={builder.isSaving}
                onClick={() => {
                  void builder.executeSave(builder.pendingSave!, []);
                  builder.setShowTemplateUpdateDialog(false);
                }}
              >
                Template only
              </button>
              <button
                type="button"
                className="lib-confirm-btn lib-confirm-btn--danger"
                disabled={builder.isSaving || builder.selectedInstanceIds.size === 0}
                onClick={() => {
                  void builder.executeSave(
                    builder.pendingSave!,
                    Array.from(builder.selectedInstanceIds),
                  );
                }}
              >
                Apply to {builder.selectedInstanceIds.size} selected
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {leaveConfirmOpen ? (
        <TrainerConfirmDialog
          open
          title="Do you want to leave your plan without saving?"
          message="Your plan draft will be lost."
          confirmLabel="Leave"
          onCancel={() => {
            setLeaveConfirmOpen(false);
            setPendingNav(null);
          }}
          onConfirm={() => {
            setLeaveConfirmOpen(false);
            const go = pendingNav;
            setPendingNav(null);
            go?.();
          }}
        />
      ) : null}

      {discardViewConfirm ? (
        <TrainerConfirmDialog
          open
          title="Do you want to leave your plan without saving?"
          message="Your plan draft will be lost."
          confirmLabel="Discard"
          onCancel={() => setDiscardViewConfirm(false)}
          onConfirm={() => {
            setDiscardViewConfirm(false);
            builder.resetToOriginal();
            builder.setMode('view');
            setModeInUrl('view');
          }}
        />
      ) : null}

      {planDeleteConfirm && !isNewPlan ? (
        <TrainerConfirmDialog
          open
          title="Do you want to delete this plan?"
          message="Your plan draft will be lost."
          confirmLabel="Delete"
          onCancel={() => setPlanDeleteConfirm(false)}
          onConfirm={() => {
            void handleConfirmDeletePlan();
          }}
        />
      ) : null}

      {duplicateToastOpen ? (
        <ExerciseEditSavedToast
          exerciseName=""
          planName={builder.planName}
          variant="plan-duplicate"
          onDismiss={() => setDuplicateToastOpen(false)}
        />
      ) : null}
    </TrainerAppLayout>
  );
};

export default WorkoutPlanDetailPage;
